import _ from "lodash";
import Cache from "core/cache/index";
import { getEnabledElement } from "component/toolManager/enabledElements.js";
import CustomEvent from "core/customEvent/index";
import Config from "config";
import assert from "core/except/assert";
import makeKey from "core/cache/KeyGen";
import pubsub from "global/global";

class WwwcTool {
  orientation: Number;
  constructor() {
    this.orientation = 0;
  }

  /**
   * @TODO : Modality에 따른 Window 값 계산 방법 정의
   * PT(PET)영상과 이외의 Modality 영상에 대한 Window 값 계산 방법정의 이며
   * 추후 Modality에 따른 추가 변경 발생이 가능하기 떄문에 기능 구현 후 추상화 방법에 대한 고민 필요
   */
  calculateVOIByDragPosDeltaAsPT = (
    voi: { windowWidth: number; windowCenter: number },
    minMax: { min: number; max: number },
    slope: number,
    intercept: number,
    delta: { x: number; y: number }
  ) => {
    // VOI window center/width 값을 이용해 PET 영상 출력 범위 Percentage 를 계산
    const pixelRange = minMax.max - minMax.min;

    const percent = !!pixelRange
      ? (((voi.windowCenter + voi.windowWidth / 2) / slope - intercept) /
          pixelRange) *
        100
      : 0; // Divided by zero 예외처리
    //const multiplier = 10000 / 1000000;
    let alpha = 1.0;
    if (percent > 200) {
      alpha = percent / 100;
    }

    const multiplier = 0.1;
    const deltaY = delta.y * multiplier * alpha;
    let displayedMaxValuePercent = 0;
    if (this.orientation === 0) {
      displayedMaxValuePercent = Math.max(
        Math.min(percent - deltaY, 10000),
        0.001
      );
    } else {
      displayedMaxValuePercent = Math.max(
        Math.min(percent + deltaY, 10000),
        0.001
      );
    }

    const supremumUnit =
      ((minMax.max * displayedMaxValuePercent) / 100) * slope + intercept;
    const infimumUnit = 0 * slope + intercept;

    const windowCenter = (supremumUnit + infimumUnit) / 2;
    //const windowWidth = Math.abs(supremumUnit - infimumUnit) / 2;
    const windowWidth = Math.abs(supremumUnit - infimumUnit);

    const recalculatedVOI = { windowWidth: 0, windowCenter: 0 };

    recalculatedVOI.windowWidth = windowWidth;
    recalculatedVOI.windowCenter = windowCenter;

    // if (voi.windowWidth < 1) {
    //   recalculatedVOI.windowWidth = 1;
    // }

    return recalculatedVOI;
  };

  calculateVOIByDragPosDeltaAsOther = (
    voi: { windowWidth: number; windowCenter: number },
    minMax: { min: number; max: number },
    slope: number,
    intercept: number,
    delta: { x: number; y: number }
  ) => {
    const minPixelValue: number = minMax.min;
    const maxPixelValue: number = minMax.max;
    const maxVOI: number = maxPixelValue * slope + intercept;
    const minVOI: number = minPixelValue * slope + intercept;
    //console.log(`maxVOI ${maxVOI}`)
    const imageDynamicRange = maxVOI - minVOI;
    const multiplier = imageDynamicRange / 1024;

    const deltaX = delta.x * multiplier;
    const deltaY = delta.y * multiplier;

    const recalculatedVOI = { windowWidth: 0, windowCenter: 0 };
    if (this.orientation === 0) {
      recalculatedVOI.windowWidth = voi.windowWidth + deltaX;
      recalculatedVOI.windowCenter = voi.windowCenter - deltaY;
    } else {
      recalculatedVOI.windowWidth = voi.windowWidth + deltaY;
      recalculatedVOI.windowCenter = voi.windowCenter + deltaX;
    }

    if (recalculatedVOI.windowWidth < 2) {
      recalculatedVOI.windowWidth = 1;
    }

    return recalculatedVOI;
  };

  calculateVOIByDragPosDelta = (
    modality: String,
    voi: { windowWidth: number; windowCenter: number },
    minMax: { min: number; max: number },
    slope: number,
    intercept: number,
    delta: { x: number; y: number }
  ) => {
    if (modality === "PT") {
      return this.calculateVOIByDragPosDeltaAsPT(
        voi,
        minMax,
        slope,
        intercept,
        delta
      );
    }
    return this.calculateVOIByDragPosDeltaAsOther(
      voi,
      minMax,
      slope,
      intercept,
      delta
    );
  };

  onMouseDrag = (evt: any) => {
    const eventData = evt.detail;
    const { element: enabledElement } = eventData;

    // 1. 마우스 이동 변위에 따른 VOI 재 계산을 위한 파라미터 값 준비
    let metadataCache = new Cache.Metadata();
    const dataId = makeKey(
      enabledElement.image.seriesInstanceUID,
      enabledElement.image.modality
    );

    let slope = enabledElement.image.rescaleSlope;
    let intercept = enabledElement.image.rescaleIntercept;
    const modality: String = enabledElement.image.modality || "";
    const voi: { windowWidth: number; windowCenter: number } = {
      windowWidth: enabledElement.viewport.voi.windowWidth,
      windowCenter: enabledElement.viewport.voi.windowCenter,
    };
    let minmax: { min: number; max: number } = {
      min: enabledElement.image.minMax[0],
      max: enabledElement.image.minMax[1],
    };
    if (modality.toLowerCase() === "pt") {
      slope = metadataCache.getSeriesMaxRescaleSlope(dataId);
      intercept = 0;
      const seriesMinMax = metadataCache.getSeriesMinMax(dataId);
      if (seriesMinMax === null) {
        return;
      }
      minmax["min"] = seriesMinMax["min"];
      minmax["max"] = seriesMinMax["max"];
    }
    // let metadataCache = new Cache.Metadata();
    // const seriesMinMax = metadataCache.getSeriesMinMax();
    // const minmax: { min: number; max: number } = {
    //   min: _.get(seriesMinMax, "min", 0),
    //   max: _.get(seriesMinMax, "max", 0),
    // };

    const delta: { x: number; y: number } = {
      x: eventData.deltaPoints.page.x,
      y: eventData.deltaPoints.page.y,
    };

    // 2. 마우스 이동 변위에 따른 VOI 재 계산 수행
    const newVOI: {
      windowWidth: number;
      windowCenter: number;
    } = this.calculateVOIByDragPosDelta(
      modality,
      voi,
      minmax,
      slope,
      intercept,
      delta
    );
    // 3. 새 VOI를 적용
    enabledElement.viewport.voi.windowWidth = newVOI.windowWidth;
    enabledElement.viewport.voi.windowCenter = newVOI.windowCenter;
    Config.rootStore.gridLayoutStore.setContrastOptionNameOnActiveDCMView("");
    Config.rootStore.toolbarStore.activateTool("Contrast");
    (Config.rootStore as any).toolbarStore.setViewportToolType(3 /*Contrast*/);
    enabledElement.needsRedraw = true;
    /**
     * @FIXME 위 setContrastOptionName 함수 실행시, 구독대상 요소에 이벤트가 발생하지 않음
     * 임시로 아래 이벤트를 전달하여 변경되게 하였음.
     */
    pubsub.publish(CustomEvent.Messages.CONTRAST_VIEWPORT, {});
    CustomEvent.triggerEvent(
      eventData.element.element,
      CustomEvent.EVENTS.WWWC_CHANGING,
      enabledElement
    );
  };

  onMouseUp = (evt: any) => {
    // 1x1
    // eslint-disable-next-line no-unused-expressions
    let focusDCMViewIdx = Config.rootStore.gridLayoutStore.getFocusOneDCMViewIdx();
    if (focusDCMViewIdx !== -1) {
      return;
    }
    const eventData = evt.detail;
    var elementStub = getEnabledElement(eventData.element.element);
    CustomEvent.triggerEvent(
      eventData.element.element,
      CustomEvent.EVENTS.WWWC_CHANGED,
      elementStub
    );
  };
}

export default WwwcTool;
